// useAddEdit.tsx - UPDATED VERSION
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { createDynamicSchema, addSchema, AddModelFormValues } from "./validations";
import {
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { routes } from "@/routes/utils";
import { toast } from "sonner";
import {
  useAddInvestorInfoMutation,
  useAddInvestorInfoImageMutation,
  useGetInvestorInfoById,
  useGetInvestorInfoImageById,
  useUpdatInvestorInfoMutation,
} from "@/services";
import { useMemo, useEffect, useState, useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  INVESTOR_TYPE,
  SUB_TYPE,
  TYPE,
  UPLOAD,
  YEAR,
  ADDITIONAL_UPLOAD,
  TITLE,
  DATE,
  DESCRIPTION,
  RATING_NAME,
  RATING_TYPE,
  RATING_DESCRIPTION,
  THUMBNAIL_UPLOAD,
  UPLOAD_TRANSCRIPT,
  UPLOAD_LOGO,
  LINK
} from "./consts";
import { getFieldConfig } from "./fieldConfig";

// Helper to normalize date value to Date object or null
const normalizeDate = (dateValue: any): Date | null => {
  if (!dateValue) return null;
  if (dateValue instanceof Date) return dateValue;
  if (typeof dateValue === "string" || typeof dateValue === "number") {
    try {
      const date = new Date(dateValue);
      return !isNaN(date.getTime()) ? date : null;
    } catch {
      return null;
    }
  }
  return null;
};

const validateImageDimensions = (
  file: File,
  requiredWidth: number,
  requiredHeight: number
): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      const isValid =
        img.width === requiredWidth && img.height === requiredHeight;
      URL.revokeObjectURL(url);
      resolve(isValid);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(false);
    };

    img.src = url;
  });
};



// Helper to compare two date values
const areDatesEqual = (date1: any, date2: any): boolean => {
  if (!date1 && !date2) return true;
  if (!date1 || !date2) return false;

  const normalized1 = normalizeDate(date1);
  const normalized2 = normalizeDate(date2);

  if (normalized1 && normalized2) {
    return normalized1.getTime() === normalized2.getTime();
  }
  return date1 === date2;
};

// Get required fields from zod schema
const getRequiredFields = (schema: any): string[] => {
  if (!schema || !schema.shape) return [];

  const requiredFields: string[] = [];
  const shape = schema.shape;

  Object.keys(shape).forEach(fieldName => {
    const field = shape[fieldName];
    if (field && field._def && field._def.typeName !== 'ZodOptional' && field._def.typeName !== 'ZodNullable') {
      requiredFields.push(fieldName);
    }
  });

  return requiredFields;
};

// Compare two subTitleData arrays
const areSubTitleDataEqual = (a: any[] = [], b: any[] = []) => {
  const normalize = (arr: any[]) =>
    (arr || []).map(i => ({ subTitle: i.subTitle || "", attachmentId: i.attachmentId || "" }));
  const na = normalize(a);
  const nb = normalize(b);
  if (na.length !== nb.length) return false;
  for (let i = 0; i < na.length; i++) {
    if (na[i].subTitle !== nb[i].subTitle || na[i].attachmentId !== nb[i].attachmentId) return false;
  }
  return true;
};

// Normalize API item to form-friendly structure
const normalizeExistingDataForForm = (data: any) => {
  if (!data) {
    return {
      investorType: "",
      investorSubType: "",
      type: "",
      title: "",
      date: null,
      year: "",
      transcriptDescription: "",
      link: "",
      thumbnailUpload: "",
      transcriptUpload: "",
      logoUpload: "",
      ratingType: "",
      ratingName: "",
      ratingDescription: "",
      upload: "",
      additionalUpload: [],
      subTitleData: []
    };
  }

  const normalizeAdditionalUpload = (subTitleData: any[]) => {
    if (!Array.isArray(subTitleData)) return [];
    return subTitleData.map(s => {
      return {
        file: {
          attachmentId: s.attachmentId,
          name: s.fileName || s.name || s.attachmentId || "File",
          url: s.url || s.fileAttachmentData?.url || undefined,
        },
        subTitle: s.subTitle || "",
      };
    });
  };

  const payload = data.data ?? data;

  return {
    investorType: payload.investorType ?? "",
    investorSubType: payload.investorSubType ?? "",
    type: payload.type ?? "",
    title: payload.title ?? "",
    date: payload.date ? new Date(payload.date) : null,
    year: payload.year?.toString() ?? "",
    transcriptDescription: payload.transcriptDescription ?? "",
    ratingType: payload.ratingType ?? "",
    ratingName: payload.ratingName ?? "",
    link: payload.link ?? "",
    ratingDescription: payload.ratingDescription ?? "",
    thumbnailUpload: payload.thumbnailAttachmentId 
      ? {
          attachmentId: payload.thumbnailAttachmentId,
          name: payload.thumbnailAttachmentData?.fileName ?? payload.fileName ?? "File",
          url: payload.thumbnailAttachmentData?.url ?? undefined,
        }
      : "",
    transcriptUpload: payload.transcriptAttachmentId
      ? {
          attachmentId: payload.transcriptAttachmentId,
          name: payload.transcriptAttachmentData?.fileName ?? payload.transcriptFileName ?? "Transcript",
          url: payload.transcriptAttachmentData?.url ?? undefined,
        }
      : "",
    logoUpload: payload.logoAttachmentId
      ? {
          attachmentId: payload.logoAttachmentId,
          name: payload.logoAttachmentData?.fileName ?? payload.logoFileName ?? "Transcript",
          url: payload.logoAttachmentData?.url ?? undefined,
        }
      : "",
    upload: payload.fileAttachmentId
      ? {
          attachmentId: payload.fileAttachmentId,
          name: payload.fileAttachmentData?.fileName ?? payload.fileName ?? "File",
          url: payload.fileAttachmentData?.url ?? undefined,
        }
      : "",
    additionalUpload: normalizeAdditionalUpload(payload.subTitleData ?? []),
    subTitleData: payload.subTitleData ?? [],
  };
};

// Custom hook for file upload with progress tracking
const useFileUploadWithProgress = () => {
  const [uploadProgress, setUploadProgress] = useState<{
    [key: string]: number;
  }>({});
  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});

  const { mutateAsync: uploadImageAsync } = useAddInvestorInfoImageMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Image upload failed",
        {
          className: "!bg-red-500 !text-white",
        }
      );
    },
  });

  const uploadFile = async (
    file: File,
    fileType: string
  ): Promise<{ attachmentId: string; url?: string }> => {
    setUploadingFiles(prev => ({ ...prev, [fileType]: true }));
    setUploadProgress(prev => ({ ...prev, [fileType]: 0 }));

    try {
      // Simulate progress (in real implementation, you'd use axios onUploadProgress)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const current = prev[fileType] || 0;
          const newProgress = current + 10;
          return { ...prev, [fileType]: newProgress > 90 ? 90 : newProgress };
        });
      }, 200);

      const formData = new FormData();
      formData.append('file', file);

      const response = await uploadImageAsync({ 
        data: { file }
      });

      clearInterval(progressInterval);
      setUploadProgress(prev => ({ ...prev, [fileType]: 100 }));

      const newAttachmentId =
        response?.data?.data?.attachmentId ?? 
        response?.data?.attachmentId ?? 
        response?.attachmentId ?? 
        null;

      if (!newAttachmentId) {
        throw new Error("Failed to upload file");
      }

      // Small delay to show 100% progress
      await new Promise(resolve => setTimeout(resolve, 300));

      return {
        attachmentId: newAttachmentId,
        url: response?.data?.data?.url ?? response?.data?.url ?? undefined,
      };

    } finally {
      setTimeout(() => {
        setUploadingFiles(prev => ({ ...prev, [fileType]: false }));
        setUploadProgress(prev => ({ ...prev, [fileType]: 0 }));
      }, 500);
    }
  };

  return {
    uploadFile,
    uploadProgress,
    uploadingFiles,
  };
};

const useAddEdit = () => {
  const { action, id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isView = action == "view";
  const isEdit = action == "edit";
  const isMobile = useIsMobile();
  const mainUploadRef = useRef<HTMLInputElement | null>(null);
  const thumbnailUploadRef = useRef<HTMLInputElement | null>(null);
  const uploadTranscriptRef = useRef<HTMLInputElement | null>(null);
  const uploadLogoRef = useRef<HTMLInputElement | null>(null);
  
  const [isViewingVideo, setIsViewingVideo] = useState(false);
  const [activePreview, setActivePreview] = useState<"thumbnail" | "main" | "additional" | "transcript" | "logo" | null>(null);
  const [isViewingPDF, setIsViewingPDF] = useState(false);

  // State for schema and initialization
  const [dynamicSchema, setDynamicSchema] = useState<any>(addSchema);
  const [isInitialized, setIsInitialized] = useState(false);

  const [showLink, setShowLink] = useState(false);
  const [openCropper, setOpenCropper] = useState(false);
  const [openImageViewer, setOpenImageViewer] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);

  // For additional-file viewer flow:
  const [additionalImageId, setAdditionalImageId] = useState<string | undefined>(undefined);

  // Use custom upload hook with progress tracking
  const { uploadFile, uploadProgress, uploadingFiles } = useFileUploadWithProgress();

  // Fetch existing data for edit/view
  const { data: existingData, isLoading: isExistingDataLoading } = useGetInvestorInfoById({
    id: id!,
    queryParams: {
      enabled: !!id && (isEdit || isView),
    },
  });

  // Normalize existing data for form use
  const normalizeExistingData = (data: any) => normalizeExistingDataForForm(data);

  // Default values
  const defaultValues = useMemo(
    () => ({
      [INVESTOR_TYPE]: "",
      [SUB_TYPE]: "",
      [TYPE]: "",
      [TITLE]: "",
      [UPLOAD]: "",
      [THUMBNAIL_UPLOAD]: "",
      [UPLOAD_TRANSCRIPT]: "",
      [UPLOAD_LOGO]: "",
      [LINK]: "",
     [YEAR]: ((d => {
        const y = d.getFullYear();
        const m = d.getMonth();
        const fyStart = m < 3 ? y - 1 : y;
        return `FY ${fyStart}-${fyStart + 1}`;
      })(new Date()))
                  ,
      [DATE]: null,
      [DESCRIPTION]: "",
      [RATING_TYPE]: "",
      [RATING_NAME]: "",
      [RATING_DESCRIPTION]: "",
      [ADDITIONAL_UPLOAD]: [],
    }),
    []
  );

  const form = useForm<AddModelFormValues>({
    resolver: zodResolver(dynamicSchema),
    defaultValues,
    mode: "onChange",
  });

  // Watch values
  const watchedValues = form.watch();

  // Initialize form with existing data for edit/view
  useEffect(() => {
    if (!(isEdit || isView) || !existingData) {
      setIsInitialized(true);
      return;
    }

    const init = async () => {
      const cleanData = normalizeExistingData(existingData);

      const schema = createDynamicSchema({
        investorType: cleanData.investorType,
        subType: cleanData.investorSubType,
        type: cleanData.type,
      });

      setDynamicSchema(schema);

      form.reset({
        [INVESTOR_TYPE]: cleanData.investorType,
        [SUB_TYPE]: cleanData.investorSubType,
        [TYPE]: cleanData.type,
        [TITLE]: cleanData.title,
        [DATE]: cleanData.date,
        [YEAR]: cleanData.year,
        [LINK]: cleanData.link,
        [DESCRIPTION]: cleanData.transcriptDescription,
        [RATING_TYPE]: cleanData.ratingType,
        [RATING_NAME]: cleanData.ratingName,
        [RATING_DESCRIPTION]: cleanData.ratingDescription,
        [UPLOAD]: cleanData.upload,
        [THUMBNAIL_UPLOAD]: cleanData.thumbnailUpload,
        [UPLOAD_TRANSCRIPT]: cleanData.transcriptUpload,
        [UPLOAD_LOGO]: cleanData.logoUpload,
        [ADDITIONAL_UPLOAD]: cleanData.additionalUpload,
      });

      setIsInitialized(true);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingData, isEdit, isView]);

  // --- EXCLUSIVE LINK OR UPLOAD LOGIC ---
useEffect(() => {
  const subType = form.watch(SUB_TYPE);
  const type = form.watch(TYPE);

  const isDisclosureLODR =
    subType === "Disclosure under Regulation 46 and 62 of SEBI (LODR)" &&
    type === "Disclosure under Regulation 46 and 62 of SEBI (LODR)";

  if (!isDisclosureLODR) return;

  const link = form.watch(LINK);
  const upload = form.watch(UPLOAD);

  // If user enters LINK → clear UPLOAD + hide upload field
  if (link && link.trim() !== "" && upload) {
    form.setValue(UPLOAD, "", { shouldValidate: true });
  }

  // If user uploads file → clear LINK + hide link field
  if (upload && typeof upload === "object" && link) {
    form.setValue(LINK, "", { shouldValidate: true });
  }
}, [
  form.watch(LINK),
  form.watch(UPLOAD),
  form.watch(SUB_TYPE),
  form.watch(TYPE)
]);


  // Fetch existing image blobs
  const {
    data: mainExistingImageBlob,
    isFetching: isMainImageLoading,
  } = useGetInvestorInfoImageById({
    id: form.watch(UPLOAD)?.attachmentId,
    queryParams: {
      enabled: !!form.watch(UPLOAD)?.attachmentId,
    },
  });

  const {
    data: mainExistingThumbnailBlob,
    isFetching: isThumbnailLoading,
  } = useGetInvestorInfoImageById({
    id: form.watch(THUMBNAIL_UPLOAD)?.attachmentId,
    queryParams: {
      enabled: !!form.watch(THUMBNAIL_UPLOAD)?.attachmentId,
    },
  });

  const {
    data: transcriptExistingBlob,
    isFetching: isTranscriptLoading,
  } = useGetInvestorInfoImageById({
    id: form.watch(UPLOAD_TRANSCRIPT)?.attachmentId,
    queryParams: {
      enabled: !!form.watch(UPLOAD_TRANSCRIPT)?.attachmentId,
    },
  });

  const {
    data: logoExistingBlob,
    isFetching: isLogoLoading,
  } = useGetInvestorInfoImageById({
    id: form.watch(UPLOAD_LOGO)?.attachmentId,
    queryParams: {
      enabled: !!form.watch(UPLOAD_LOGO)?.attachmentId,
    },
  });

  // Fetch additional image blob when user requests it
  const {
    data: additionalImageBlob,
    isFetching: isAdditionalImageLoading,
  } = useGetInvestorInfoImageById({
    id: additionalImageId ?? "",
    queryParams: {
      enabled: !!additionalImageId,
    },
  });

  // Manage object URLs to ensure cleanup
  useEffect(() => {
    if (mainExistingImageBlob && activePreview === "main") {
      const url = URL.createObjectURL(mainExistingImageBlob);
      setImageSrc(url);
      
      const name = form.getValues(UPLOAD)?.name?.toLowerCase() || "";
      if (mainExistingImageBlob.type === "application/pdf" || name.endsWith(".pdf")) {
        setIsViewingPDF(true);
        setIsViewingVideo(false);
      }
    }
  }, [mainExistingImageBlob, activePreview, form]);

  useEffect(() => {
    if (mainExistingThumbnailBlob && activePreview === "thumbnail") {
      const url = URL.createObjectURL(mainExistingThumbnailBlob);
      setImageSrc(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [mainExistingThumbnailBlob, activePreview]);

  useEffect(() => {
    if (transcriptExistingBlob && activePreview === "transcript") {
      const url = URL.createObjectURL(transcriptExistingBlob);
      setImageSrc(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [transcriptExistingBlob, activePreview]);

  useEffect(() => {
    if (logoExistingBlob && activePreview === "logo") {
      const url = URL.createObjectURL(logoExistingBlob);
      setImageSrc(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [logoExistingBlob, activePreview]);

  // When additional image blob arrives, open viewer
  useEffect(() => {
    if (additionalImageBlob && activePreview === "additional") {
      const url = URL.createObjectURL(additionalImageBlob);
      setImageSrc(url);
      setOpenImageViewer(true);
      return () => URL.revokeObjectURL(url);
    }
  }, [additionalImageBlob, activePreview]);

  // Set existing files for view mode
  useEffect(() => {
    if (!existingData?.data?.fileAttachmentId) return;

    form.setValue(UPLOAD, {
      attachmentId: existingData.data.fileAttachmentId,
      name: existingData.data.fileAttachmentData?.fileName ?? "File",
      url: existingData.data.fileAttachmentData?.url,
    });

    setShowLink(true);
  }, [existingData, form]);

  useEffect(() => {
    if (!existingData?.data?.thumbnailAttachmentId) return;

    form.setValue(THUMBNAIL_UPLOAD, {
      attachmentId: existingData.data.thumbnailAttachmentId,
      name: existingData.data.thumbnailAttachmentData?.fileName ?? "File",
      url: existingData.data.thumbnailAttachmentData?.url,
    });

    setShowLink(true);
  }, [existingData, form]);

  // Update schema when form values change (for add mode)
  useEffect(() => {
    if (isEdit || isView) return;

    const schema = createDynamicSchema({
      investorType: watchedValues[INVESTOR_TYPE],
      subType: watchedValues[SUB_TYPE],
      type: watchedValues[TYPE],
    });

    setDynamicSchema(schema);
  }, [
    watchedValues[INVESTOR_TYPE],
    watchedValues[SUB_TYPE],
    watchedValues[TYPE],
    isEdit,
    isView,
  ]);

  // Get field configuration
  const fieldConfig = useMemo(() => {
    if (!isEdit && !isView) {
      return getFieldConfig(
        watchedValues[INVESTOR_TYPE],
        watchedValues[SUB_TYPE],
        watchedValues[TYPE]
      );
    }

    if (!isInitialized || !existingData) return null;

    const clean = normalizeExistingData(existingData);

    return getFieldConfig(
      clean.investorType,
      clean.investorSubType,
      clean.type
    );
  }, [
    watchedValues[INVESTOR_TYPE],
    watchedValues[SUB_TYPE],
    watchedValues[TYPE],
    existingData,
    isInitialized,
    isEdit,
    isView,
  ]);

  const requiredFields = getRequiredFields(dynamicSchema);

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.investorInfoMBAPL}`);
  };

  // Add / remove additional upload helpers
  const addAdditionalUpload = () => {
    const currentUploads = form.getValues(ADDITIONAL_UPLOAD) || [];
    form.setValue(ADDITIONAL_UPLOAD, [
      ...currentUploads,
      { file: null, subTitle: "" },
    ]);
  };

  const removeAdditionalUpload = (index: number) => {
    const currentUploads = form.getValues(ADDITIONAL_UPLOAD) || [];
    const newUploads = currentUploads.filter((_, i) => i !== index);
    form.setValue(ADDITIONAL_UPLOAD, newUploads);
  };

  // Mutation hooks
  const { mutateAsync: addInfoAsync, isPending: isAddPending } =
    useAddInvestorInfoMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.issues?.error === "string"
            ? error?.response?.data?.issues?.error
            : typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
          {
            className: "!bg-red-500 !text-white",
          }
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Investor Information Added Successfully", {
          className: "!bg-green-600 !text-white",
        });
        queryClient.invalidateQueries({
          queryKey: ["investor-info-mba-pl"],
        });
        onBackClick();
      },
    });

  const { mutateAsync: updateInfoAsync, isPending: isUpdatePending } =
    useUpdatInvestorInfoMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.issues?.error === "string"
            ? error?.response?.data?.issues?.error
            : typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
          {
            className: "!bg-red-500 !text-white",
          }
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Investor Information Updated Successfully", {
          className: "!bg-green-600 !text-white",
        });
        queryClient.invalidateQueries({
          queryKey: ["investor-info-mba-pl"],
        });
        onBackClick();
      },
    });

  // onSubmit with progress tracking
  const onSubmit = async (data: AddModelFormValues) => {
    try {
      let _data: any = removeEmptyFields(data);
      _data = setEmptyFieldsToNull(_data);

      // Validate main upload if required

      if (fieldConfig?.fields.includes(THUMBNAIL_UPLOAD) && !_data[THUMBNAIL_UPLOAD]) {
        form.setError(THUMBNAIL_UPLOAD, {
          type: "manual",
          message: "This Field is required",
        });
        return;
      }

      // Prepare base payload values
      const basePayload: any = {
        investorType: _data[INVESTOR_TYPE],
        investorSubType: _data[SUB_TYPE],
        link: _data[LINK],
        type: _data[TYPE],
        title: _data[TITLE],
        date: _data[DATE],
        year: _data[YEAR] || undefined,
        transcriptDescription: _data[DESCRIPTION],
        ratingType: _data[RATING_TYPE],
        ratingName: _data[RATING_NAME],
        ratingDescription: _data[RATING_DESCRIPTION],
      };

      // Upload subtitle files (if any)
      const additionalUploads = _data[ADDITIONAL_UPLOAD] ?? [];
      const uploadsToProcess = additionalUploads.map((u: any) => ({ ...u }));

      // 1) Upload new subtitle files
      const subtitleUploadPromises: Promise<any>[] = [];
      uploadsToProcess.forEach((u: any, idx: number) => {
        if (u.file instanceof File) {
          subtitleUploadPromises.push(
            uploadFile(u.file, `subtitle-${idx}`).then((result) => {
              uploadsToProcess[idx].file = {
                attachmentId: result.attachmentId,
                name: u.file.name,
                url: result.url,
              };
              return uploadsToProcess[idx];
            })
          );
        }
      });

      // 2) Upload main file if it's a File instance
      let mainFileAttachmentId: string | undefined;
      const uploadValue = _data[UPLOAD];
      const isMainNewFile = uploadValue instanceof File;
      const isMainExistingFile = uploadValue && typeof uploadValue === "object" && "attachmentId" in uploadValue;

      const mainUploadPromise = isMainNewFile
        ? uploadFile(uploadValue, "main").then((result) => {
            mainFileAttachmentId = result.attachmentId;
            return result.attachmentId;
          })
        : Promise.resolve(null);

      // 3) Upload thumbnail file if it's a File instance
      let mainThumbnailAttachmentId: string | undefined;
      const uploadThumbnailValue = _data[THUMBNAIL_UPLOAD];
      const isThumbnailNewFile = uploadThumbnailValue instanceof File;
      const isThumbnailExistingFile = uploadThumbnailValue && typeof uploadThumbnailValue === "object" && "attachmentId" in uploadThumbnailValue;

      const thumbnailUploadPromise = isThumbnailNewFile
        ? uploadFile(uploadThumbnailValue, "thumbnail").then((result) => {
            mainThumbnailAttachmentId = result.attachmentId;
            return result.attachmentId;
          })
        : Promise.resolve(null);

      // 4) Upload transcript file if it's a File instance
      let transcriptAttachmentId: string | undefined;
      const uploadTranscriptValue = _data[UPLOAD_TRANSCRIPT];
      const isTranscriptNewFile = uploadTranscriptValue instanceof File;
      const isTranscriptExistingFile = uploadTranscriptValue && typeof uploadTranscriptValue === "object" && "attachmentId" in uploadTranscriptValue;

      const transcriptUploadPromise = isTranscriptNewFile
        ? uploadFile(uploadTranscriptValue, "transcript").then((result) => {
            transcriptAttachmentId = result.attachmentId;
            return result.attachmentId;
          })
        : Promise.resolve(null);

      // 5) Upload logo file if it's a File instance
      let logoAttachmentId: string | undefined;
      const uploadLogoValue = _data[UPLOAD_LOGO];
      const isLogoNewFile = uploadLogoValue instanceof File;
      const isLogoExistingFile = uploadLogoValue && typeof uploadLogoValue === "object" && "attachmentId" in uploadLogoValue;

      const logoUploadPromise = isLogoNewFile
        ? uploadFile(uploadLogoValue, "logo").then((result) => {
            logoAttachmentId = result.attachmentId;
            return result.attachmentId;
          })
        : Promise.resolve(null);

      // Wait for all uploads in parallel
      await Promise.all([
        mainUploadPromise,
        thumbnailUploadPromise,
        transcriptUploadPromise,
        logoUploadPromise,
        ...subtitleUploadPromises,
      ]);

      // Build final subTitleData
      const finalSubTitleData = uploadsToProcess
        .map((u: any) => {
          const att = u?.file?.attachmentId;
          if (!att) return null;
          return {
            subTitle: u.subTitle || "",
            attachmentId: att,
          };
        })
        .filter(Boolean);

      // Now proceed to either Add or Update
      if (isEdit && id) {
        // Update mode
        const updatePayload: any = {};
        const existing = normalizeExistingData(existingData);

        // Compare and include only changed scalar fields
        if (basePayload.investorType !== existing.investorType) {
          updatePayload.investorType = basePayload.investorType;
        }
        if (basePayload.investorSubType !== undefined && basePayload.investorSubType !== existing.investorSubType) {
          updatePayload.investorSubType = basePayload.investorSubType;
        }
        if (basePayload.type !== existing.type) {
          updatePayload.type = basePayload.type;
        }
        if (basePayload.title !== existing.title) {
          updatePayload.title = basePayload.title;
        }
        if (!areDatesEqual(basePayload.date, existing.date)) {
          updatePayload.date = basePayload.date;
        }
        if (basePayload.year !== undefined && basePayload.year?.toString() !== existing.year) {
          updatePayload.year = basePayload.year;
        }
        if ((basePayload.transcriptDescription ?? "") !== (existing.transcriptDescription ?? "")) {
          updatePayload.transcriptDescription = basePayload.transcriptDescription;
        }
        if ((basePayload.link ?? "") !== (existing.link ?? "")) {
          updatePayload.link = basePayload.link;
        }
        if ((basePayload.ratingType ?? "") !== (existing.ratingType ?? "")) {
          updatePayload.ratingType = basePayload.ratingType;
        }
        if ((basePayload.ratingName ?? "") !== (existing.ratingName ?? "")) {
          updatePayload.ratingName = basePayload.ratingName;
        }
        if ((basePayload.ratingDescription ?? "") !== (existing.ratingDescription ?? "")) {
          updatePayload.ratingDescription = basePayload.ratingDescription;
        }

        // Handle file updates
        if (isThumbnailNewFile && mainThumbnailAttachmentId) {
          updatePayload.thumbnailAttachmentId = mainThumbnailAttachmentId;
        } else if (isThumbnailExistingFile && uploadThumbnailValue.attachmentId) {
          if (uploadThumbnailValue.attachmentId !== existingData?.data?.thumbnailAttachmentId) {
            updatePayload.thumbnailAttachmentId = uploadThumbnailValue.attachmentId;
          }
        }

        if (isTranscriptNewFile && transcriptAttachmentId) {
          updatePayload.transcriptAttachmentId = transcriptAttachmentId;
        } else if (isTranscriptExistingFile && uploadTranscriptValue.attachmentId) {
          if (uploadTranscriptValue.attachmentId !== existingData?.data?.transcriptAttachmentId) {
            updatePayload.transcriptAttachmentId = uploadTranscriptValue.attachmentId;
          }
        }

        if (isLogoNewFile && logoAttachmentId) {
          updatePayload.logoAttachmentId = logoAttachmentId;
        } else if (isLogoExistingFile && uploadLogoValue.attachmentId) {
          if (uploadLogoValue.attachmentId !== existingData?.data?.logoAttachmentId) {
            updatePayload.logoAttachmentId = uploadLogoValue.attachmentId;
          }
        }

        if (isMainNewFile && mainFileAttachmentId) {
          updatePayload.fileAttachmentId = mainFileAttachmentId;
        } else if (isMainExistingFile && uploadValue.attachmentId) {
          if (uploadValue.attachmentId !== existingData?.data?.fileAttachmentId) {
            updatePayload.fileAttachmentId = uploadValue.attachmentId;
          }
        }

        // subTitleData handling
        const existingSubTitleData = existing.subTitleData ?? [];
        if (!areSubTitleDataEqual(existingSubTitleData, finalSubTitleData)) {
          updatePayload.subTitleData = finalSubTitleData;
        }

        // Clean payload
        const cleanPayload = Object.fromEntries(
          Object.entries(updatePayload).filter(
            ([_, value]) =>
              value !== undefined && value !== "" && value !== null
          )
        );

        if (Object.keys(cleanPayload).length > 0) {
          await updateInfoAsync({
            id,
            data: cleanPayload,
          });
        } else {
          toast("No changes detected");
        }
      } else {
        // Add mode
        const finalMainAttachmentId =
          mainFileAttachmentId ??
          (isMainExistingFile ? _data[UPLOAD]?.attachmentId : undefined);

        const finalthumbnailAttachmentId = 
          mainThumbnailAttachmentId ??
          (isThumbnailExistingFile ? _data[THUMBNAIL_UPLOAD]?.attachmentId : undefined);

        const finalTranscriptAttachmentId =
          transcriptAttachmentId ??
          (isTranscriptExistingFile ? _data[UPLOAD_TRANSCRIPT]?.attachmentId : undefined);

        const finalLogoAttachmentId =
          logoAttachmentId ??
          (isLogoExistingFile ? _data[UPLOAD_LOGO]?.attachmentId : undefined);

        const payload: any = {
          ...basePayload,
          ...(finalMainAttachmentId ? { fileAttachmentId: finalMainAttachmentId } : {}),
          ...(finalthumbnailAttachmentId ? { thumbnailAttachmentId: finalthumbnailAttachmentId } : {}),
          ...(finalTranscriptAttachmentId ? { transcriptAttachmentId: finalTranscriptAttachmentId } : {}),
          ...(finalLogoAttachmentId ? { logoAttachmentId: finalLogoAttachmentId } : {}),
          ...(finalSubTitleData.length ? { subTitleData: finalSubTitleData } : {}),
        };

        const cleanPayload = Object.fromEntries(
          Object.entries(payload).filter(
            ([_, value]) =>
              value !== undefined && value !== "" && value !== null
          )
        );

        await addInfoAsync({
          data: cleanPayload,
        });
      }
    } catch (err: any) {
      console.error("Submit error:", err);
      toast.error(err?.message ?? "Something went wrong during submission", {
        className: "!bg-red-500 !text-white",
      });
    }
  };

  // Local file change (main file) preview handling
  const onFileChange = async (file: File, fieldName?: string) => {
  if (!file) return;

  // ✅ STRICT 284 × 284 VALIDATION FOR LOGO
  if (fieldName === UPLOAD_LOGO) {
    const isValid = await validateImageDimensions(file, 284, 284);

    if (!isValid) {
      form.setError(UPLOAD_LOGO, {
        type: "manual",
        message: "Logo must be exactly 284 × 284 pixels",
      });

      // ❌ RESET FILE
      uploadLogoRef.current!.value = "";
      return; // ⛔ HARD STOP
    }

    // ✅ CLEAR ERROR IF VALID
    form.clearErrors(UPLOAD_LOGO);
  }

  // ✅ SET FILE INTO REACT-HOOK-FORM (THIS WAS MISSING)
  if (fieldName) {
    form.setValue(fieldName as any, file, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }

  // ✅ PREVIEW
  const reader = new FileReader();
  reader.onload = () => {
    setImageSrc(reader.result as string);
  };
  reader.readAsDataURL(file);

  setOpenCropper(true);
  setShowLink(false);
};



  // View functions
  const onViewThumbnail = () => {
    setActivePreview("thumbnail");
    setIsViewingVideo(false);
    setIsViewingPDF(false);
    setOpenImageViewer(true);
  };

  const onViewTranscript = () => {
    setActivePreview("transcript");
    const transcriptValue = form.getValues(UPLOAD_TRANSCRIPT);
    const name = transcriptValue?.name?.toLowerCase() || "";
    const isPDF = name.endsWith(".pdf");
    setIsViewingPDF(isPDF);
    setIsViewingVideo(false);
    setOpenImageViewer(true);
  };

  const onViewLogo = () => {
    setActivePreview("logo");
    setIsViewingPDF(false);
    setIsViewingVideo(false);
    setOpenImageViewer(true);
  };

  const onViewImage = () => {
    setActivePreview("main");
    const uploadValue = form.getValues(UPLOAD);
    const name = uploadValue?.name?.toLowerCase() || "";

    const isVideo =
      name.endsWith(".mp4") ||
      name.endsWith(".webm") ||
      name.endsWith(".ogg");

    const isPDF = name.endsWith(".pdf");

    setIsViewingVideo(isVideo);
    setIsViewingPDF(isPDF);
    setOpenImageViewer(true);
  };

  const onCloseCropper = () => {
    setOpenCropper(false);
    form.setValue(UPLOAD, "");
    form.setValue(THUMBNAIL_UPLOAD, "");
  };

  const viewAdditionalFile = (attachmentId: string) => {
    setActivePreview("additional");
    setAdditionalImageId(attachmentId);
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel: onBackClick,
    isAddPending: isAddPending,
    isMobile,
    uploadRef: mainUploadRef,
    thumbnailUploadRef,
    uploadTranscriptRef,
    uploadLogoRef,
    isAddImagePending: false,
    action,
    isUpdatePending: isUpdatePending,
    isEdit,
    addAdditionalUpload,
    removeAdditionalUpload,
    fieldConfig,
    watchedValues,
    isExistingDataLoading: isExistingDataLoading || (isEdit && !isInitialized),
    existingData,
    existingImage: mainExistingImageBlob || mainExistingThumbnailBlob || transcriptExistingBlob || logoExistingBlob,
    isInitialized,
    onViewImage,
    onFileChange,
    onCloseCropper,
    openImageViewer,
    openCropper,
    showLink,
    setOpenImageViewer,
    imageSrc,
    isImageLoading: isMainImageLoading || isAdditionalImageLoading || isThumbnailLoading || isTranscriptLoading || isLogoLoading,
    viewAdditionalFile,
    isViewingVideo,
    onViewThumbnail,
    onViewTranscript,
    onViewLogo,
    isViewingPDF,
    // Add progress tracking states
    uploadProgress,
    uploadingFiles,
    isViewingTranscript: false,
    transcriptExistingBlob,
  };
};

export default useAddEdit;